import { reactive } from 'vue'

// App-wide toast queue, rendered once by AppToaster.vue.
//
// Replaces toastify-js, which mounted its own detached nodes with inline
// styles and could not be made to sit above the bottom navigation or inside
// the safe area on notched phones.

let nextId = 1

export const toastState = reactive({
  items: [],
})

const DEFAULT_DURATION = 3200
const MAX_VISIBLE = 3

export const dismissToast = id => {
  const index = toastState.items.findIndex(item => item.id === id)
  if (index === -1) return
  const [removed] = toastState.items.splice(index, 1)
  if (removed?.timer) clearTimeout(removed.timer)
}

export const clearToasts = () => {
  toastState.items.forEach(item => item.timer && clearTimeout(item.timer))
  toastState.items.splice(0)
}

export function showToast(message, type = 'default', options = {}) {
  if (!message) return null

  const text = String(message)

  // The lock screens can fire the same message from a retry loop. Refresh the
  // existing toast instead of stacking duplicates on top of each other.
  const existing = toastState.items.find(item => item.text === text && item.type === type)
  if (existing) {
    if (existing.timer) clearTimeout(existing.timer)
    existing.count += 1
    // Same rule as a fresh toast: duration 0 means sticky. Scheduling
    // setTimeout(…, 0) here dismissed a repeat of a sticky toast on the next
    // tick, which is the opposite of what asking for sticky means.
    const repeatDuration = options.duration ?? DEFAULT_DURATION
    existing.timer = repeatDuration > 0 ? setTimeout(() => dismissToast(existing.id), repeatDuration) : null
    return existing.id
  }

  const id = nextId++
  const duration = options.duration ?? DEFAULT_DURATION

  const item = reactive({
    id,
    text,
    type,
    action: options.action || null,
    count: 1,
    timer: null,
  })

  // Sticky toasts (duration 0) are for things the user must act on, so they
  // are exempt from the queue cap.
  if (duration > 0) item.timer = setTimeout(() => dismissToast(id), duration)

  toastState.items.push(item)

  while (toastState.items.filter(entry => entry.timer).length > MAX_VISIBLE) {
    const oldest = toastState.items.find(entry => entry.timer)
    if (!oldest) break
    dismissToast(oldest.id)
  }

  return id
}

export const toast = {
  show: showToast,
  success: (message, options) => showToast(message, 'success', options),
  error: (message, options) => showToast(message, 'error', options),
  warning: (message, options) => showToast(message, 'warning', options),
  info: (message, options) => showToast(message, 'info', options),
  dismiss: dismissToast,
  clear: clearToasts,
}

export default toast
